import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import LegacyFALogo from '../components/ui/LegacyFALogo';
import * as pdfjsLib from 'pdfjs-dist';
import {
  X, Save, ChevronLeft, ChevronRight, ChevronDown,
  Minus, Plus,
  PenTool, Edit3, Calendar, Mail,
  Type, CheckSquare, FileText, Check,
  User, Building2, Briefcase, Stamp,
} from 'lucide-react';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000/api/v1';

const FIELD_TYPES = [
  { type: 'signature', label: 'Signature', icon: PenTool, w: 150, h: 63, iconColor: 'text-teal-500 bg-teal-50' },
  { type: 'date_signed', label: 'Date Signed', icon: Calendar, w: 110, h: 22, iconColor: 'text-teal-500 bg-teal-50' },
  { type: 'date', label: 'Date', icon: Calendar, w: 110, h: 22, iconColor: 'text-teal-500 bg-teal-50' },
  { type: 'textfield', label: 'Text', icon: Type, w: 130, h: 22, iconColor: 'text-teal-500 bg-teal-50' },
  { type: 'email', label: 'Email Address', icon: Mail, w: 150, h: 22, iconColor: 'text-teal-500 bg-teal-50' },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare, w: 18, h: 18, iconColor: 'text-teal-500 bg-teal-50' },
];


const SigningSpacePage = () => {
  const { documentId, bundleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isBundle = !!bundleId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bundleData, setBundleData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [activeDocIndex, setActiveDocIndex] = useState(0);
  const [doc, setDoc] = useState(null);
  const [pdfPages, setPdfPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [placedFields, setPlacedFields] = useState([]);
  const [selectedFieldType, setSelectedFieldType] = useState(null);
  const [selectedRecipientId, setSelectedRecipientId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [autoSaved, setAutoSaved] = useState(true);
  const [showViewDocs, setShowViewDocs] = useState(false);

  const pdfContainerRef = useRef(null);
  const pageRefs = useRef({});

  useEffect(() => { if (isBundle) loadBundle(); else loadSingleDocument(documentId); }, [documentId, bundleId]); // eslint-disable-line

  const loadBundle = async () => {
    setLoading(true);
    try {
      const res = await api.getBundleStatus(bundleId);
      const b = res.bundle || res;
      setBundleData(b); setDocuments(b.documents || []);
      if ((b.recipients || []).length > 0) setSelectedRecipientId(b.recipients[0].id);
      if ((b.documents || []).length > 0) { setActiveDocIndex(0); await loadDocumentPDF(b.documents[0].id); }
    } catch (err) { setError(err.message || 'Failed to load bundle'); } finally { setLoading(false); }
  };

  const loadSingleDocument = async (docId) => {
    setLoading(true);
    try {
      const res = await api.getDocument(docId);
      const d = res.document || res;
      setDoc(d); setDocuments([{ id: d.id, title: d.title, status: d.status, total_pages: d.total_pages }]); setActiveDocIndex(0);
      if (d.recipients?.length > 0) setSelectedRecipientId(d.recipients[0].id || d.recipients[0].recipient_id);
      if (d.signature_fields?.length > 0) setPlacedFields(d.signature_fields.map(f => ({ id: f.id || f.field_id, type: f.field_type, page: f.page_number, x: f.x_position || 0, y: f.y_position || 0, w: f.width || 150, h: f.height || 63, recipientId: f.recipient_id, docId, saved: true })));
      await loadPDFForDoc(docId);
    } catch (err) { setError(err.message || 'Failed to load document'); } finally { setLoading(false); }
  };

  const loadDocumentPDF = async (docId) => {
    try {
      const res = await api.getDocument(docId); const d = res.document || res; setDoc(d);
      if (!selectedRecipientId && d.recipients?.length > 0) setSelectedRecipientId(d.recipients[0].id || d.recipients[0].recipient_id);
      if (d.signature_fields?.length > 0) setPlacedFields(prev => [...prev.filter(f => f.docId !== docId), ...d.signature_fields.map(f => ({ id: f.id || f.field_id, type: f.field_type, page: f.page_number, x: f.x_position || 0, y: f.y_position || 0, w: f.width || 150, h: f.height || 63, recipientId: f.recipient_id, docId, saved: true }))]);
    } catch {} await loadPDFForDoc(docId);
  };

  const loadPDFForDoc = async (docId) => {
    const authToken = api.getToken(); setPdfPages([]); setCurrentPage(1);
    try {
      const pdf = await pdfjsLib.getDocument({ url: `${API_BASE}/documents/${docId}/preview`, httpHeaders: authToken ? { 'Authorization': `Bearer ${authToken}` } : {} }).promise;
      const pages = []; for (let i = 1; i <= pdf.numPages; i++) pages.push(await pdf.getPage(i)); setPdfPages(pages);
    } catch (err) { console.error('Failed to load PDF:', err); }
  };

  const switchDocument = async (index) => { if (index === activeDocIndex) return; setActiveDocIndex(index); setShowViewDocs(false); if (documents[index]) await loadDocumentPDF(documents[index].id); };

  const scrollToPage = useCallback((n) => { const el = pageRefs.current[n]; if (el && pdfContainerRef.current) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, []);
  const goToPage = useCallback((n) => { const c = Math.max(1, Math.min(n, pdfPages.length)); setCurrentPage(c); scrollToPage(c); }, [pdfPages.length, scrollToPage]);

  useEffect(() => {
    const container = pdfContainerRef.current; if (!container || pdfPages.length === 0) return;
    const onScroll = () => { const mid = container.getBoundingClientRect().top + container.getBoundingClientRect().height / 2; let closest = 1, best = Infinity; for (let p = 1; p <= pdfPages.length; p++) { const el = pageRefs.current[p]; if (el) { const d = Math.abs(el.getBoundingClientRect().top + el.getBoundingClientRect().height / 2 - mid); if (d < best) { best = d; closest = p; } } } setCurrentPage(closest); };
    container.addEventListener('scroll', onScroll, { passive: true }); return () => container.removeEventListener('scroll', onScroll);
  }, [pdfPages.length]);

  const activeDocId = documents[activeDocIndex]?.id || documentId;

  const handlePageClick = useCallback((e, pageNumber) => {
    if (!selectedFieldType || !selectedRecipientId) return;
    const rect = e.currentTarget.getBoundingClientRect(); const x = (e.clientX - rect.left) / scale; const y = (e.clientY - rect.top) / scale;
    const fd = FIELD_TYPES.find(f => f.type === selectedFieldType); if (!fd) return;
    setPlacedFields(prev => [...prev, { id: `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`, type: selectedFieldType, page: pageNumber, x: Math.max(0, x - fd.w / 2), y: Math.max(0, y - fd.h / 2), w: fd.w, h: fd.h, recipientId: selectedRecipientId, docId: activeDocId, saved: false }]);
    setAutoSaved(false);
  }, [selectedFieldType, selectedRecipientId, scale, activeDocId]);

  const removeField = async (fieldId) => { const f = placedFields.find(f => f.id === fieldId); if (f?.saved) { try { await api.deleteSignatureField(f.docId || activeDocId, fieldId); } catch {} } setPlacedFields(prev => prev.filter(f => f.id !== fieldId)); setAutoSaved(false); };

  const moveField = useCallback((fieldId, newX, newY) => {
    setPlacedFields(prev => prev.map(f => f.id === fieldId ? { ...f, x: newX, y: newY, saved: false } : f));
    setAutoSaved(false);
  }, []);

  const saveFields = async () => {
    setSaving(true);
    try { for (const field of placedFields.filter(f => !f.saved)) { try { const r = await api.addSignatureField(field.docId || activeDocId, { recipient_id: field.recipientId, field_type: field.type, page_number: field.page, x_position: Math.round(field.x), y_position: Math.round(field.y), width: Math.round(field.w), height: Math.round(field.h) }); const sid = r.field?.id || r.id; if (sid) setPlacedFields(prev => prev.map(f => f.id === field.id ? { ...f, id: sid, saved: true } : f)); } catch {} } setAutoSaved(true); } finally { setSaving(false); }
  };

  useEffect(() => { if (placedFields.some(f => !f.saved)) { const t = setTimeout(() => saveFields(), 2000); return () => clearTimeout(t); } }, [placedFields]); // eslint-disable-line

  const handleSend = async () => { if (placedFields.some(f => !f.saved)) await saveFields(); setSending(true); try { if (doc?.signing_links?.length > 0) { const t = doc.signing_links[0].signing_url?.split('/').pop(); if (t) { navigate(`/sign/${t}`); return; } } navigate(isBundle ? '/dashboard' : `/documents/${documentId}`); } catch (err) { alert('Failed: ' + err.message); } finally { setSending(false); } };
  const handleClose = () => navigate('/dashboard');

  const totalPages = pdfPages.length;
  const recipients = isBundle ? (bundleData?.recipients || []) : (doc?.recipients || []);
  const title = isBundle ? (bundleData?.title || 'Bundle') : (doc?.title || '-');
  const createdAtFormatted = isBundle ? (bundleData?.created_at_formatted || '-') : (doc?.created_at_formatted || '-');
  const expiresAtFormatted = isBundle ? (bundleData?.expires_at_formatted || '-') : (doc?.expires_at_formatted || '-');
  const currentDocFields = placedFields.filter(f => f.docId === activeDocId);

  useEffect(() => { if (!showViewDocs) return; const close = () => setShowViewDocs(false); document.addEventListener('click', close); return () => document.removeEventListener('click', close); }, [showViewDocs]);

  const getInitials = (name) => { if (!name) return '?'; return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2); };

  if (loading) return (<div className="h-screen flex items-center justify-center bg-gray-50"><div className="text-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600 mx-auto mb-4" /><p className="text-gray-500 text-sm">Loading signing space…</p></div></div>);
  if (error) return (<div className="h-screen flex items-center justify-center bg-gray-50"><div className="bg-white border border-red-200 text-red-700 px-8 py-6 rounded-xl shadow-sm max-w-md text-center"><p className="font-semibold mb-2">Error</p><p className="text-sm">{error}</p><button onClick={() => navigate('/dashboard')} className="mt-4 text-sm text-teal-700 underline">Back to Dashboard</button></div></div>);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50">

      {/* ═══ Navigation bar — consistent with Dashboard / Document Detail ═══ */}
      <header className="bg-white border-b border-gray-200 flex-shrink-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-3">
          <div className="flex items-center cursor-pointer" onClick={handleClose}>
            <LegacyFALogo size="sm" />
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-bold">
                {getInitials(user?.name || user?.email)}
              </div>
              <span className="text-sm text-gray-700 hidden md:block">{user?.name || user?.email}</span>
            </div>
          </div>
        </div>
      </header>

      {/* ═══ Teal info bar — document metadata + Send ═══ */}
      <div className="bg-teal-700 text-white flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between">
          <div className="hidden lg:flex items-center space-x-8 text-xs">
            {[
              { label: 'Subject', value: title?.toUpperCase() },
              { label: 'Created Date', value: createdAtFormatted },
              { label: 'Expired Date', value: expiresAtFormatted },
              { label: 'Enclosed Documents', value: <>{documents.length} <Edit3 className="h-3 w-3 inline ml-1 opacity-50" /></> },
              { label: 'Envelope Recipients', value: <>{recipients.length} <Edit3 className="h-3 w-3 inline ml-1 opacity-50" /></> },
            ].map((item, i) => (
              <div key={i}>
                <p className="text-teal-300 text-[10px]">{item.label}</p>
                <p className="font-bold">{item.value}</p>
              </div>
            ))}
          </div>
          <button onClick={handleSend} disabled={sending} className="px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white rounded-lg text-sm font-medium flex items-center space-x-1.5 disabled:opacity-50 transition-colors ml-auto">
            <span>Send</span><ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* ═══ Main: floating sidebar card + content ═══ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-1 overflow-hidden py-6 gap-5 w-full">

        {/* ─── Sidebar — floating white card ─── */}
        <div className="w-56 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col flex-shrink-0 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-800">Fields</h3>
          </div>

          {recipients.length > 1 && (
            <div className="px-4 py-3 border-b border-gray-100">
              <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1">For Recipient</label>
              <select value={selectedRecipientId || ''} onChange={(e) => setSelectedRecipientId(e.target.value)} className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-teal-500 focus:border-teal-500 bg-gray-50">
                {recipients.map((r, i) => (<option key={r.id || r.recipient_id} value={r.id || r.recipient_id}>{r.name || `Recipient ${i + 1}`}</option>))}
              </select>
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-3 py-4 space-y-2.5">
            {FIELD_TYPES.map((ft) => {
              const Icon = ft.icon;
              const isSelected = selectedFieldType === ft.type;
              return (
                <button
                  key={ft.type}
                  onClick={() => setSelectedFieldType(isSelected ? null : ft.type)}
                  className={`w-full h-12 flex items-center space-x-3 px-3 rounded-xl text-sm text-left transition-all ${
                    isSelected
                      ? 'bg-teal-50/70 border border-teal-400 text-teal-700 font-semibold'
                      : 'border text-gray-700'
                  }`}
                  style={!isSelected ? {
                    background: 'rgba(249,249,249,0.2)',
                    borderColor: '#F1F1F4',
                    boxShadow: '0 3px 4px 0 rgba(0,0,0,0.03)',
                  } : undefined}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-white text-teal-600 border border-teal-200' : 'bg-gray-100 text-gray-400'
                  }`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <span className="font-medium">{ft.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ─── Content area ─── */}
        <div className="flex-1 flex flex-col overflow-hidden">

          {/* Toolbar row 1: page nav + View Docs / Prev / Next */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Page {currentPage} out of {totalPages || '?'}</span>
              <div className="flex items-center space-x-0.5 ml-2">
                <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1} className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:bg-white hover:text-gray-600 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                {Array.from({ length: Math.min(totalPages, 8) }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => goToPage(p)} className={`w-7 h-7 rounded text-xs font-semibold transition-colors ${currentPage === p ? 'bg-teal-700 text-white' : 'text-gray-600 hover:bg-white'}`}>{p}</button>
                ))}
                <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages} className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:bg-white hover:text-gray-600 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
              </div>

              {/* Zoom */}
              <div className="flex items-center space-x-1 ml-4">
                <button onClick={() => setScale(s => Math.max(0.5, +(s - 0.1).toFixed(1)))} className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded bg-white hover:bg-gray-50"><Minus className="h-3.5 w-3.5 text-gray-500" /></button>
                <span className="text-xs text-gray-600 font-medium w-11 text-center">{Math.round(scale * 100)}%</span>
                <button onClick={() => setScale(s => Math.min(2, +(s + 0.1).toFixed(1)))} className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded bg-white hover:bg-gray-50"><Plus className="h-3.5 w-3.5 text-gray-500" /></button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* View Documents dropdown */}
              <div className="relative">
                <button onClick={(e) => { e.stopPropagation(); setShowViewDocs(v => !v); }} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 bg-white hover:bg-gray-50 flex items-center font-medium">
                  View Documents <ChevronDown className="h-3 w-3 ml-1.5 text-gray-400" />
                </button>
                {showViewDocs && (
                  <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-lg z-40 py-1 max-h-72 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                    <div className="px-4 py-2 text-[10px] text-gray-400 font-semibold uppercase tracking-wider border-b border-gray-100">{documents.length} document(s) enclosed</div>
                    {documents.map((d, idx) => (
                      <button key={d.id} onClick={() => switchDocument(idx)} className={`w-full text-left px-4 py-2.5 flex items-center space-x-3 transition-colors ${activeDocIndex === idx ? 'bg-teal-50' : 'hover:bg-gray-50'}`}>
                        <div className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">{idx + 1}</div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm truncate ${activeDocIndex === idx ? 'text-teal-700 font-semibold' : 'text-gray-700'}`}>{d.title || `Document ${idx + 1}`}</p>
                          <p className="text-[11px] text-gray-400">{d.total_pages || '?'} page(s)</p>
                        </div>
                        {activeDocIndex === idx && <Check className="h-4 w-4 text-teal-600 flex-shrink-0" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => switchDocument(activeDocIndex - 1)} disabled={activeDocIndex <= 0} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-30 flex items-center font-medium"><ChevronLeft className="h-3 w-3 mr-0.5" /> Prev</button>
              <button onClick={() => switchDocument(activeDocIndex + 1)} disabled={activeDocIndex >= documents.length - 1} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-30 flex items-center font-medium">Next <ChevronRight className="h-3 w-3 ml-0.5" /></button>
            </div>
          </div>

          {/* PDF scrollable area */}
          <div ref={pdfContainerRef} className="flex-1 overflow-auto bg-gray-200 rounded-xl">
            {selectedFieldType && (
              <div className="text-center py-3 sticky top-0 z-10">
                <span className="inline-flex items-center px-4 py-2 bg-white text-teal-700 rounded-full text-xs font-medium shadow border border-teal-100">
                  <PenTool className="h-3.5 w-3.5 mr-2 text-teal-500" />
                  Click on the document to place a <strong className="ml-1">{FIELD_TYPES.find(f => f.type === selectedFieldType)?.label}</strong>&nbsp;field
                </span>
              </div>
            )}
            <div className="flex flex-col items-center py-6 space-y-6">
              {pdfPages.map((page, idx) => (
                <div key={idx} ref={(el) => { pageRefs.current[idx + 1] = el; }}>
                  <PDFPageCanvas page={page} pageNumber={idx + 1} scale={scale} fields={currentDocFields.filter(f => f.page === idx + 1)} onPageClick={handlePageClick} onRemoveField={removeField} onMoveField={moveField} selectedFieldType={selectedFieldType} selectedRecipientId={selectedRecipientId} />
                </div>
              ))}
              {pdfPages.length === 0 && (<div className="bg-white rounded-xl shadow p-16 text-center text-gray-400"><FileText className="h-14 w-14 mx-auto mb-4 opacity-30" /><p className="text-sm">PDF preview not available</p></div>)}
            </div>
          </div>

          {/* Status bar */}
          <div className="mt-2 flex justify-between items-center text-xs text-gray-500">
            <span>Doc {activeDocIndex + 1} of {documents.length}: {documents[activeDocIndex]?.title || 'Document'} — {currentDocFields.length} field(s) placed</span>
            <span>{currentPage} of {totalPages || '?'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const PDFPageCanvas = ({ page, pageNumber, scale, fields, onPageClick, onRemoveField, onMoveField, selectedFieldType, selectedRecipientId }) => {
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const dragRef = useRef(null);

  useEffect(() => { render(); return () => { if (renderTaskRef.current) { renderTaskRef.current.cancel(); renderTaskRef.current = null; } }; }, [page, scale]); // eslint-disable-line

  const render = async () => {
    if (!page || !canvasRef.current) return;
    if (renderTaskRef.current) { renderTaskRef.current.cancel(); renderTaskRef.current = null; }
    try { const vp = page.getViewport({ scale }); const c = canvasRef.current; c.width = vp.width; c.height = vp.height; setDims({ width: vp.width, height: vp.height }); const task = page.render({ canvasContext: c.getContext('2d'), viewport: vp }); renderTaskRef.current = task; await task.promise; renderTaskRef.current = null; } catch (err) { if (err?.name !== 'RenderingCancelledException') console.error(err); }
  };

  const handleDragStart = useCallback((e, field) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    dragRef.current = { fieldId: field.id, startMouseX: e.clientX, startMouseY: e.clientY, startX: field.x, startY: field.y };

    const onMouseMove = (ev) => {
      if (!dragRef.current) return;
      const dx = (ev.clientX - dragRef.current.startMouseX) / scale;
      const dy = (ev.clientY - dragRef.current.startMouseY) / scale;
      const newX = Math.max(0, dragRef.current.startX + dx);
      const newY = Math.max(0, dragRef.current.startY + dy);
      onMoveField(dragRef.current.fieldId, newX, newY);
    };

    const onMouseUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [scale, onMoveField]);

  const RECIPIENT_COLORS = [
    'border-blue-400 bg-blue-50/80',
    'border-orange-400 bg-orange-50/80',
    'border-emerald-400 bg-emerald-50/80',
    'border-purple-400 bg-purple-50/80',
    'border-pink-400 bg-pink-50/80',
    'border-cyan-400 bg-cyan-50/80',
    'border-yellow-400 bg-yellow-50/80',
    'border-red-400 bg-red-50/80',
  ];
  const recipientIds = useRef([]);
  const getRecipientColor = (recipientId) => {
    let idx = recipientIds.current.indexOf(String(recipientId));
    if (idx === -1) { recipientIds.current.push(String(recipientId)); idx = recipientIds.current.length - 1; }
    return RECIPIENT_COLORS[idx % RECIPIENT_COLORS.length];
  };

  return (
    <div ref={containerRef} className={`relative bg-white shadow-lg ${selectedFieldType ? 'cursor-crosshair' : ''}`} style={{ width: dims.width || 'auto', height: dims.height || 'auto' }} onClick={(e) => { if (e.target === canvasRef.current || e.target === e.currentTarget) onPageClick(e, pageNumber); }}>
      <canvas ref={canvasRef} className="block" />
      {fields.map((f) => {
        const Icon = FIELD_TYPES.find(ft => ft.type === f.type)?.icon || Type;
        const isOwned = String(f.recipientId) === String(selectedRecipientId);
        return (
          <div
            key={f.id}
            className={`absolute border-2 rounded flex items-center justify-center ${isOwned ? 'group cursor-grab active:cursor-grabbing' : 'cursor-default'} ${getRecipientColor(f.recipientId)} ${!isOwned ? 'opacity-40' : ''}`}
            style={{ left: f.x * scale, top: f.y * scale, width: f.w * scale, height: f.h * scale, zIndex: 10 }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={isOwned ? (e) => handleDragStart(e, f) : undefined}
          >
            <div className="flex items-center space-x-1 text-[10px] opacity-60 pointer-events-none select-none"><Icon className="h-2.5 w-2.5" /><span className="truncate">{FIELD_TYPES.find(ft => ft.type === f.type)?.label}</span></div>
            {isOwned && (
              <button onClick={(e) => { e.stopPropagation(); onRemoveField(f.id); }} onMouseDown={(e) => e.stopPropagation()} className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 shadow"><X className="h-2.5 w-2.5" /></button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SigningSpacePage;
